import { StanCard, StanTable } from '@/components';
import type { StanColumnDef } from '@/components/core-components/StanTable/StanTable.types';
import { useMultipleChartData } from '../queries/useAnalyticsQueries';
import { CHART_TYPES, type ProductAnalyticsRow, type ProductAnalyticsTableProps } from '../types/analytics.types';
import { formatCurrency } from '@/lib';
import { useUser } from '@/queries/useUser';
import {
    calculateAnalyticsRange,
    calculatePeriodLength,
    getEndDateTimestamp,
    transformProductAnalytics,
    transformProductAnalyticsData,
} from '../logic/analytics.logic';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useMemo } from 'react';

/**
 * Creates analytics table columns configuration
 * Extracted to component level to handle JSX properly
 */
const createAnalyticsColumns = (currencyCode: string): StanColumnDef<ProductAnalyticsRow>[] => [
    {
        accessorKey: 'product',
        header: 'Product',
        cell: info => {
            const value: string = info.getValue();
            return <div className='text-dark font-medium text-wrap'>{value}</div>;
        },
        width: '40%',
    },
    {
        accessorKey: 'views',
        header: 'Views',
        cell: info => {
            const value: number = info.getValue();
            return <div className='text-dark font-medium'>{value > 0 ? value.toLocaleString() : '-'}</div>;
        },
        hideOnMobile: true,
        width: '15%',
    },
    {
        accessorKey: 'orders',
        header: 'Orders',
        cell: info => {
            const value: number = info.getValue();
            return <div className='text-dark font-medium'>{value > 0 ? value.toLocaleString() : '-'}</div>;
        },
        width: '15%',
    },
    {
        accessorKey: 'conversion',
        header: 'Conversion %',
        cell: info => {
            const value = info.getValue() as number;
            return <div className='text-dark font-medium'>{value > 0 ? `${value.toFixed(1)}%` : '-'}</div>;
        },
        width: '15%',
        hideOnMobile: true,
    },
    {
        accessorKey: 'revenue',
        header: 'Revenue',
        cell: info => (
            <div className='text-dark font-medium'>
                {info.row.original.revenue > 0 ? (
                    <span className='text-success-main font-bold'>
                        {formatCurrency(info.getValue() as number, currencyCode)}
                    </span>
                ) : (
                    '-'
                )}
            </div>
        ),
        align: 'right',
    },
];

export function ProductAnalyticsTable({ dateRange }: ProductAnalyticsTableProps) {
    const { user } = useUser();
    const { push } = useStanRouter();
    const analyticsRange = calculateAnalyticsRange(dateRange);
    const endDateTimestamp = getEndDateTimestamp(dateRange.endDate);
    const periodLength = calculatePeriodLength(analyticsRange);

    // Fetch product analytics data with transformation
    const chartTypes = useMemo(
        () => [
            CHART_TYPES.PAGE_CONVERSION,
            CHART_TYPES.PAGE_TRAFFIC_BREAKDOWN,
            CHART_TYPES.REVENUE,
            CHART_TYPES.ORDERS,
        ],
        []
    );
    const { data, isLoading } = useMultipleChartData(chartTypes, analyticsRange, endDateTimestamp, periodLength);
    const transformedData = data ? transformProductAnalytics(data) : null;
    const processedData = transformedData ? transformProductAnalyticsData(transformedData) : null;

    const products = processedData?.products ?? [];

    const currencyCode = user?.data?.currency || 'usd';
    const columns = useMemo(() => createAnalyticsColumns(currencyCode), [currencyCode]);

    const handleRowClick = useMemo(
        () => (row: ProductAnalyticsRow) => {
            const routeData = processedData?.pagesRouteData?.[row.product];
            if (routeData?.page_id) {
                push(`/stores/${routeData.store_id}/page/${routeData.page_type}/${routeData.page_id}`);
            }
        },
        [processedData?.pagesRouteData, push]
    );

    return (
        <StanCard>
            <StanTable<ProductAnalyticsRow>
                columns={columns}
                data={products}
                isLoading={isLoading}
                onRowClick={handleRowClick}
                emptyHeader='No Data To Show Yet'
                emptySubheader='Learn how to improve your business metrics <a href="https://help.stan.store/article/137-how-to-drive-more-stan-store-sales" target="_blank">here</a>!'
            />
        </StanCard>
    );
}
